/**
 * Minimal QR Code generator — produces SVG string.
 * Supports alphanumeric mode, error correction level L.
 * Based on QR Code Model 2 spec (ISO/IEC 18004).
 *
 * For our use case (URL ~120 chars) we need version 4-6.
 */

// ─── Reed-Solomon & QR internals ─────────────────────────────

const ECL_L = 0;

// Capacities for byte mode, ECL L (versions 1-10)
const CAPACITIES = [0, 17, 32, 53, 78, 106, 134, 154, 192, 230, 271];

// Error correction codewords per block for ECL L (versions 1-10)
const EC_CODEWORDS = [0, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18];
const NUM_BLOCKS = [0, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4];

function getVersion(dataLen: number): number {
	for (let v = 1; v <= 10; v++) {
		if (CAPACITIES[v]! >= dataLen) return v;
	}
	return 10; // fallback
}

function getModuleCount(version: number): number {
	return 17 + version * 4;
}

// GF(256) arithmetic for Reed-Solomon
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
	let x = 1;
	for (let i = 0; i < 255; i++) {
		GF_EXP[i] = x;
		GF_LOG[x] = i;
		x = x << 1;
		if (x & 256) x ^= 0x11d;
	}
	for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
	if (a === 0 || b === 0) return 0;
	return GF_EXP[(GF_LOG[a]! + GF_LOG[b]!) % 255]!;
}

function rsEncode(data: number[], ecLen: number): number[] {
	// Generate polynomial
	const gen: number[] = new Array(ecLen + 1).fill(0);
	gen[0] = 1;
	for (let i = 0; i < ecLen; i++) {
		for (let j = ecLen; j >= 1; j--) {
			gen[j] = gen[j - 1]! ^ gfMul(gen[j]!, GF_EXP[i]!);
		}
		gen[0] = gfMul(gen[0]!, GF_EXP[i]!);
	}

	const msg = [...data, ...new Array(ecLen).fill(0)];
	for (let i = 0; i < data.length; i++) {
		const coef = msg[i]!;
		if (coef !== 0) {
			for (let j = 0; j <= ecLen; j++) {
				msg[i + j] ^= gfMul(gen[ecLen - j]!, coef);
			}
		}
	}
	return msg.slice(data.length);
}

// ─── QR Matrix Construction ──────────────────────────────────

function createMatrix(size: number): boolean[][] {
	return Array.from({ length: size }, () => new Array(size).fill(false));
}

function setModule(matrix: boolean[][], row: number, col: number, value: boolean) {
	if (row >= 0 && row < matrix.length && col >= 0 && col < matrix[0]!.length) {
		matrix[row]![col] = value;
	}
}

function addFinderPattern(matrix: boolean[][], reserved: boolean[][], row: number, col: number) {
	for (let r = -1; r <= 7; r++) {
		for (let c = -1; c <= 7; c++) {
			const rr = row + r;
			const cc = col + c;
			if (rr < 0 || rr >= matrix.length || cc < 0 || cc >= matrix.length) continue;
			const isBlack =
				(r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
				(c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
				(r >= 2 && r <= 4 && c >= 2 && c <= 4);
			matrix[rr]![cc] = isBlack;
			reserved[rr]![cc] = true;
		}
	}
}

function addAlignmentPattern(matrix: boolean[][], reserved: boolean[][], row: number, col: number) {
	for (let r = -2; r <= 2; r++) {
		for (let c = -2; c <= 2; c++) {
			const isBlack = Math.max(Math.abs(r), Math.abs(c)) !== 1;
			matrix[row + r]![col + c] = isBlack;
			reserved[row + r]![col + c] = true;
		}
	}
}

// Alignment pattern positions for versions 2-10
const ALIGNMENT_POSITIONS: number[][] = [
	[], // v0 placeholder
	[], // v1 — no alignment
	[6, 18],
	[6, 22],
	[6, 26],
	[6, 30],
	[6, 34],
	[6, 22, 38],
	[6, 24, 42],
	[6, 26, 46],
	[6, 28, 52],
];

function addTimingPatterns(matrix: boolean[][], reserved: boolean[][], size: number) {
	for (let i = 8; i < size - 8; i++) {
		const isBlack = i % 2 === 0;
		matrix[6]![i] = isBlack;
		reserved[6]![i] = true;
		matrix[i]![6] = isBlack;
		reserved[i]![6] = true;
	}
}

function addFormatInfo(matrix: boolean[][], reserved: boolean[][], maskPattern: number) {
	const size = matrix.length;
	// Format info: ECL L (01) + mask pattern (3 bits)
	const formatBits = getFormatBits(ECL_L, maskPattern);

	for (let i = 0; i <= 5; i++) {
		matrix[8]![i] = !!((formatBits >> (14 - i)) & 1);
		reserved[8]![i] = true;
	}
	matrix[8]![7] = !!((formatBits >> 8) & 1);
	reserved[8]![7] = true;
	matrix[8]![8] = !!((formatBits >> 7) & 1);
	reserved[8]![8] = true;
	matrix[7]![8] = !!((formatBits >> 6) & 1);
	reserved[7]![8] = true;
	for (let i = 0; i <= 5; i++) {
		matrix[5 - i]![8] = !!((formatBits >> (i)) & 1);
		reserved[5 - i]![8] = true;
	}

	// Second copy
	for (let i = 0; i <= 7; i++) {
		matrix[size - 1 - i]![8] = !!((formatBits >> i) & 1);
		reserved[size - 1 - i]![8] = true;
	}
	for (let i = 0; i <= 7; i++) {
		matrix[8]![size - 8 + i] = !!((formatBits >> (14 - i)) & 1);
		reserved[8]![size - 8 + i] = true;
	}
	// Dark module
	matrix[size - 8]![8] = true;
	reserved[size - 8]![8] = true;
}

function getFormatBits(ecl: number, mask: number): number {
	const eclBits = [1, 0, 3, 2]; // L, M, Q, H
	let data = (eclBits[ecl]! << 3) | mask;
	let rem = data;
	for (let i = 0; i < 10; i++) {
		rem = (rem << 1) ^ ((rem >> 9) * 0x537);
	}
	const bits = ((data << 10) | rem) ^ 0x5412;
	return bits;
}

function applyMask(matrix: boolean[][], reserved: boolean[][], pattern: number): boolean[][] {
	const size = matrix.length;
	const result = matrix.map((row) => [...row]);
	for (let r = 0; r < size; r++) {
		for (let c = 0; c < size; c++) {
			if (reserved[r]![c]) continue;
			let flip = false;
			switch (pattern) {
				case 0: flip = (r + c) % 2 === 0; break;
				case 1: flip = r % 2 === 0; break;
				case 2: flip = c % 3 === 0; break;
				case 3: flip = (r + c) % 3 === 0; break;
				case 4: flip = (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0; break;
				case 5: flip = ((r * c) % 2) + ((r * c) % 3) === 0; break;
				case 6: flip = (((r * c) % 2) + ((r * c) % 3)) % 2 === 0; break;
				case 7: flip = (((r + c) % 2) + ((r * c) % 3)) % 2 === 0; break;
			}
			if (flip) result[r]![c] = !result[r]![c];
		}
	}
	return result;
}

function penaltyScore(matrix: boolean[][]): number {
	const size = matrix.length;
	let score = 0;

	// Rule 1: consecutive same-color modules in row/column
	for (let r = 0; r < size; r++) {
		let count = 1;
		for (let c = 1; c < size; c++) {
			if (matrix[r]![c] === matrix[r]![c - 1]) {
				count++;
				if (count === 5) score += 3;
				else if (count > 5) score += 1;
			} else {
				count = 1;
			}
		}
	}
	for (let c = 0; c < size; c++) {
		let count = 1;
		for (let r = 1; r < size; r++) {
			if (matrix[r]![c] === matrix[r - 1]![c]) {
				count++;
				if (count === 5) score += 3;
				else if (count > 5) score += 1;
			} else {
				count = 1;
			}
		}
	}

	return score;
}

function placeData(matrix: boolean[][], reserved: boolean[][], bits: number[]) {
	const size = matrix.length;
	let bitIdx = 0;
	let upward = true;

	for (let col = size - 1; col >= 1; col -= 2) {
		if (col === 6) col = 5; // Skip timing pattern column
		const rows = upward
			? Array.from({ length: size }, (_, i) => size - 1 - i)
			: Array.from({ length: size }, (_, i) => i);

		for (const row of rows) {
			for (let c = 0; c <= 1; c++) {
				const cc = col - c;
				if (reserved[row]![cc]) continue;
				matrix[row]![cc] = bitIdx < bits.length ? !!bits[bitIdx] : false;
				bitIdx++;
			}
		}
		upward = !upward;
	}
}

// ─── Public API ──────────────────────────────────────────────

export function generateQrSvg(text: string, cellSize = 4, margin = 2): string {
	const data = new TextEncoder().encode(text);
	const version = getVersion(data.length);
	const size = getModuleCount(version);

	// Encode data
	const dataCodewords = encodeData(data, version);

	// Add error correction
	const ecLen = EC_CODEWORDS[version]!;
	const numBlocks = NUM_BLOCKS[version]!;
	const blockSize = Math.floor(dataCodewords.length / numBlocks);

	const dataBlocks: number[][] = [];
	const ecBlocks: number[][] = [];
	let offset = 0;
	for (let b = 0; b < numBlocks; b++) {
		const thisBlockSize = b < numBlocks - (dataCodewords.length % numBlocks || numBlocks)
			? blockSize
			: blockSize + (dataCodewords.length % numBlocks ? 1 : 0);
		const block = dataCodewords.slice(offset, offset + thisBlockSize);
		dataBlocks.push(block);
		ecBlocks.push(rsEncode(block, ecLen));
		offset += thisBlockSize;
	}

	// Interleave
	const allBits: number[] = [];
	const maxDataLen = Math.max(...dataBlocks.map((b) => b.length));
	for (let i = 0; i < maxDataLen; i++) {
		for (const block of dataBlocks) {
			if (i < block.length) {
				for (let bit = 7; bit >= 0; bit--) allBits.push((block[i]! >> bit) & 1);
			}
		}
	}
	for (let i = 0; i < ecLen; i++) {
		for (const block of ecBlocks) {
			if (i < block.length) {
				for (let bit = 7; bit >= 0; bit--) allBits.push((block[i]! >> bit) & 1);
			}
		}
	}

	// Build matrix
	const matrix = createMatrix(size);
	const reserved = createMatrix(size);

	// Finder patterns
	addFinderPattern(matrix, reserved, 0, 0);
	addFinderPattern(matrix, reserved, 0, size - 7);
	addFinderPattern(matrix, reserved, size - 7, 0);

	// Reserve format info areas
	for (let i = 0; i < 9; i++) {
		reserved[8]![i] = true;
		reserved[i]![8] = true;
		if (i < 8) {
			reserved[8]![size - 1 - i] = true;
			reserved[size - 1 - i]![8] = true;
		}
	}

	// Timing
	addTimingPatterns(matrix, reserved, size);

	// Alignment patterns
	const positions = ALIGNMENT_POSITIONS[version] ?? [];
	for (const r of positions) {
		for (const c of positions) {
			// Skip if overlaps with finder patterns
			if (r <= 8 && c <= 8) continue;
			if (r <= 8 && c >= size - 8) continue;
			if (r >= size - 8 && c <= 8) continue;
			addAlignmentPattern(matrix, reserved, r, c);
		}
	}

	// Place data
	placeData(matrix, reserved, allBits);

	// Try all mask patterns, pick best
	let bestMask = 0;
	let bestScore = Infinity;
	for (let m = 0; m < 8; m++) {
		const masked = applyMask(matrix, reserved, m);
		const score = penaltyScore(masked);
		if (score < bestScore) {
			bestScore = score;
			bestMask = m;
		}
	}

	const finalMatrix = applyMask(matrix, reserved, bestMask);
	addFormatInfo(finalMatrix, reserved, bestMask);

	// Generate SVG
	const totalSize = (size + margin * 2) * cellSize;
	let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalSize} ${totalSize}" width="${totalSize}" height="${totalSize}">`;
	svg += `<rect width="${totalSize}" height="${totalSize}" fill="white"/>`;
	svg += '<path d="';

	for (let r = 0; r < size; r++) {
		for (let c = 0; c < size; c++) {
			if (finalMatrix[r]![c]) {
				const x = (c + margin) * cellSize;
				const y = (r + margin) * cellSize;
				svg += `M${x},${y}h${cellSize}v${cellSize}h-${cellSize}z`;
			}
		}
	}

	svg += '" fill="black"/></svg>';
	return svg;
}

function encodeData(data: Uint8Array, version: number): number[] {
	const totalCodewords = getTotalCodewords(version);
	const ecLen = EC_CODEWORDS[version]!;
	const numBlocks = NUM_BLOCKS[version]!;
	const dataCapacity = totalCodewords - ecLen * numBlocks;

	// Byte mode indicator (0100) + character count
	const bits: number[] = [];

	// Mode: byte (0100)
	bits.push(0, 1, 0, 0);

	// Character count (8 bits for versions 1-9, 16 for 10+)
	const countBits = version <= 9 ? 8 : 16;
	for (let i = countBits - 1; i >= 0; i--) {
		bits.push((data.length >> i) & 1);
	}

	// Data
	for (const byte of data) {
		for (let i = 7; i >= 0; i--) {
			bits.push((byte >> i) & 1);
		}
	}

	// Terminator (up to 4 bits of 0)
	const maxBits = dataCapacity * 8;
	for (let i = 0; i < 4 && bits.length < maxBits; i++) {
		bits.push(0);
	}

	// Pad to byte boundary
	while (bits.length % 8 !== 0 && bits.length < maxBits) {
		bits.push(0);
	}

	// Pad bytes
	const padBytes = [0xec, 0x11];
	let padIdx = 0;
	while (bits.length < maxBits) {
		const pb = padBytes[padIdx % 2]!;
		for (let i = 7; i >= 0; i--) bits.push((pb >> i) & 1);
		padIdx++;
	}

	// Convert to bytes
	const codewords: number[] = [];
	for (let i = 0; i < bits.length; i += 8) {
		let byte = 0;
		for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i + j] ?? 0);
		codewords.push(byte);
	}

	return codewords.slice(0, dataCapacity);
}

function getTotalCodewords(version: number): number {
	// Total codewords for versions 1-10
	const totals = [0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346];
	return totals[version] ?? 292;
}
